import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Text } from "@core/index";
import { FileUploader } from "@core/file-uploader";
import { CEFRSelector } from "@core/cefr-selector";
import {
  VisualMatchingCreateRequestDtoCefrLevel,
  VisualMatchingCreateRequestDtoLanguage,
  VisualMatchingResponseDtoStatus,
} from "@packages/api/madlenAPI.schemas";
import {
  useCreateVisualMatching,
  useGetVisualMatching,
} from "@packages/api/madlenAPI";
import { SimpleLoading } from "@core/loading";
import ErrorComponent from "@features/tools/components/error-component";

const CreateVisualMatchingView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // We declare formData with an explicit type
  type FormDataType = {
    cefrLevel: VisualMatchingCreateRequestDtoCefrLevel;
    topic: string;
    teacherDocumentId?: number;
    language: VisualMatchingCreateRequestDtoLanguage;
  };

  const [formData, setFormData] = useState<FormDataType>({
    cefrLevel: VisualMatchingCreateRequestDtoCefrLevel.PRE_A1,
    topic: "",
    teacherDocumentId: undefined,
    language: VisualMatchingCreateRequestDtoLanguage.en,
  });

  const [generationMethod, setGenerationMethod] = useState<"prompt" | "file">(
    "prompt",
  );

  const [questionCount, setQuestionCount] = useState<number>(1);
  const [optionCount, setOptionCount] = useState<number>(2);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Instead of destructuring isLoading, we'll store the entire mutation object.
  const createMutation = useCreateVisualMatching();
  const { mutateAsync: createVisualMatchingMutate } = createMutation;
  const isMutating = createMutation.status === "pending";

  // After creation, poll until READY then navigate to detail. No inline preview.

  const [createdId, setCreatedId] = useState<number | undefined>();
  const {
    data: createdData,
    isLoading: isFetching,
    error: fetchError,
  } = useGetVisualMatching(createdId!, {
    query: {
      enabled: !!createdId,
      refetchInterval: (data: any) => {
        if (
          data?.status === VisualMatchingResponseDtoStatus.READY ||
          data?.status === VisualMatchingResponseDtoStatus.FAILED
        ) {
          return false;
        }
        return 2000;
      },
    },
  });

  useEffect(() => {
    if (
      createdId &&
      createdData?.status === VisualMatchingResponseDtoStatus.READY
    ) {
      navigate(`/content/visual-matching/${createdId}`);
    }
  }, [createdId, createdData?.status, navigate]);

  if (
    isMutating ||
    (createdId &&
      (isFetching ||
        createdData?.status === VisualMatchingResponseDtoStatus.PROCESSING))
  ) {
    return <SimpleLoading duration={20} />;
  }

  if (
    createdId &&
    (fetchError ||
      createdData?.status === VisualMatchingResponseDtoStatus.FAILED)
  ) {
    return (
      <ErrorComponent
        title={t("visualMatching.create.error.title")}
        description={t("visualMatching.create.error.description")}
      />
    );
  }

  // Remove handleLevelChange function

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (generationMethod === "prompt" && !(formData.topic || "").trim()) {
      newErrors.prompt = t("common.required");
    }
    if (generationMethod === "file" && !formData.teacherDocumentId) {
      newErrors.teacherDocumentId = t("common.required");
    }

    if (!questionCount || questionCount < 1) {
      newErrors.questionCount = t("common.required");
    }
    if (!optionCount || optionCount < 1) {
      newErrors.optionCount = t("common.required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  async function handleSubmit() {
    if (!validateForm()) return;

    try {
      const result = await createVisualMatchingMutate({
        data: {
          cefrLevel: formData.cefrLevel,
          generationType:
            generationMethod === "prompt" ? "PROMPT" : "TEACHER_DOCUMENT",
          topic: generationMethod === "prompt" ? formData.topic : undefined,
          teacherDocumentId:
            generationMethod === "file"
              ? formData.teacherDocumentId
              : undefined,
          questionCount,
          optionCount,
          generateImages: true,
          language: formData.language,
        },
      });
      if (result.id) {
        setCreatedId(result.id);
      }
    } catch (error) {
      console.error("Error creating visual matching:", error);
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-gray-50 dark:bg-dark-500 mt-5 rounded-tl-3xl">
      <div className="flex items-center px-6 pt-8 pb-4">
        <button
          onClick={() => navigate("/content/visual-matching")}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mr-4 transition-colors"
        ></button>
        <Text variant="h1" className="text-2xl font-semibold flex-1">
          {t("visualMatching.create.title")}
        </Text>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-8">
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
            <div className="flex items-center mb-6">
              <div>
                <Text
                  variant="h2"
                  className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                >
                  {t("visualMatching.create.generation.title")}
                </Text>
                <Text
                  variant="p"
                  className="text-sm text-gray-600 dark:text-gray-400"
                >
                  {t("visualMatching.create.generation.description")}
                </Text>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <button
                onClick={() => setGenerationMethod("prompt")}
                className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                  generationMethod === "prompt"
                    ? "border-amber-500 bg-amber-50 dark:bg-amber-900/20 shadow-sm"
                    : "border-gray-200 dark:border-dark-400 hover:border-gray-300 dark:hover:border-dark-300"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      generationMethod === "prompt"
                        ? "bg-amber-500 text-white"
                        : "bg-gray-100 dark:bg-dark-500 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div
                      className={`font-medium ${
                        generationMethod === "prompt"
                          ? "text-amber-700 dark:text-amber-300"
                          : "text-gray-900 dark:text-gray-100"
                      }`}
                    >
                      {t("visualMatching.create.generation.promptOption")}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t("visualMatching.create.generation.promptHint")}
                    </div>
                  </div>
                </div>
                {generationMethod === "prompt" && (
                  <div className="absolute top-2 right-2 w-3 h-3 bg-amber-500 rounded-full"></div>
                )}
              </button>

              <button
                onClick={() => setGenerationMethod("file")}
                className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                  generationMethod === "file"
                    ? "border-amber-500 bg-amber-50 dark:bg-amber-900/20 shadow-sm"
                    : "border-gray-200 dark:border-dark-400 hover:border-gray-300 dark:hover:border-dark-300"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      generationMethod === "file"
                        ? "bg-amber-500 text-white"
                        : "bg-gray-100 dark:bg-dark-500 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div
                      className={`font-medium ${
                        generationMethod === "file"
                          ? "text-amber-700 dark:text-amber-300"
                          : "text-gray-900 dark:text-gray-100"
                      }`}
                    >
                      {t("visualMatching.create.generation.fileOption")}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t("visualMatching.create.generation.fileHint")}
                    </div>
                  </div>
                </div>
                {generationMethod === "file" && (
                  <div className="absolute top-2 right-2 w-3 h-3 bg-amber-500 rounded-full"></div>
                )}
              </button>
            </div>

            {generationMethod === "prompt" && (
              <div className="space-y-3">
                <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                  <svg
                    className="w-4 h-4 mr-2 text-amber-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                  {t("visualMatching.create.generation.promptLabel")} *
                </label>
                <textarea
                  value={formData.topic}
                  onChange={(e) =>
                    setFormData({ ...formData, topic: e.target.value })
                  }
                  placeholder={t(
                    "visualMatching.create.generation.promptPlaceholder",
                  )}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent dark:bg-dark-500 dark:border-dark-500 dark:text-white transition-colors ${
                    errors.prompt
                      ? "border-red-500"
                      : "border-gray-300 dark:border-dark-400"
                  }`}
                  rows={4}
                />
                {errors.prompt && (
                  <Text
                    variant="p"
                    className="text-red-500 text-sm flex items-center gap-1"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {errors.prompt}
                  </Text>
                )}
              </div>
            )}

            {generationMethod === "file" && (
              <div className="space-y-3">
                <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                  <svg
                    className="w-4 h-4 mr-2 text-amber-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  {t("visualMatching.create.generation.uploadFileLabel")} *
                </label>
                <div className="border-2 border-dashed border-gray-300 dark:border-dark-400 rounded-lg p-6 bg-gray-50 dark:bg-dark-500">
                  <FileUploader
                    onUploadSuccess={(_unused, response) =>
                      setFormData({
                        ...formData,
                        teacherDocumentId: response?.id,
                      })
                    }
                  />
                </div>
                {errors.teacherDocumentId && (
                  <Text
                    variant="p"
                    className="text-red-500 text-sm flex items-center gap-1"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {errors.teacherDocumentId}
                  </Text>
                )}
              </div>
            )}
          </div>

          {/* CEFR Level Selection */}
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
            <div className="flex items-center mb-6">
              <div>
                <Text
                  variant="h2"
                  className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                >
                  {t("visualMatching.create.cefrLevel.title")}
                </Text>
                <Text
                  variant="p"
                  className="text-sm text-gray-600 dark:text-gray-400"
                >
                  {t("visualMatching.create.cefrLevel.description")}
                </Text>
              </div>
            </div>

            <CEFRSelector
              selectedLevel={formData.cefrLevel}
              onLevelChange={(level) =>
                setFormData({ ...formData, cefrLevel: level })
              }
              themeColor="orange"
              className="mt-4"
            />
          </div>

          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
            <Text
              variant="h2"
              className="text-lg font-semibold mb-6 text-gray-900 dark:text-gray-100"
            >
              {t("visualMatching.create.questionSettings.title")}
            </Text>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Question Count */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("visualMatching.create.questionSettings.questionCount")}
                </label>
                <div className="flex items-center bg-gray-100 dark:bg-dark-500 rounded-lg">
                  <button
                    onClick={() => setQuestionCount((q) => Math.max(1, q - 1))}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-400 transition-colors"
                    disabled={questionCount <= 1}
                  >
                    -
                  </button>
                  <span className="flex-1 text-center text-lg font-semibold text-gray-900 dark:text-gray-100 py-2">
                    {questionCount}
                  </span>
                  <button
                    onClick={() => setQuestionCount((q) => Math.min(8, q + 1))}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-400 transition-colors"
                    disabled={questionCount >= 8}
                  >
                    +
                  </button>
                </div>
                <Text
                  variant="p"
                  className="text-xs text-gray-500 dark:text-gray-400"
                >
                  {t("visualMatching.create.questionSettings.maxQuestions")}: 8
                </Text>
                {errors.questionCount && (
                  <Text variant="p" className="text-red-500 text-sm">
                    {errors.questionCount}
                  </Text>
                )}
              </div>

              {/* Option Count */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("visualMatching.create.questionSettings.optionCount")}
                </label>
                <div className="flex items-center bg-gray-100 dark:bg-dark-500 rounded-lg">
                  <button
                    onClick={() => setOptionCount((o) => Math.max(1, o - 1))}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-400 transition-colors"
                    disabled={optionCount <= 1}
                  >
                    -
                  </button>
                  <span className="flex-1 text-center text-lg font-semibold text-gray-100 py-2">
                    {optionCount}
                  </span>
                  <button
                    onClick={() => setOptionCount((o) => Math.min(5, o + 1))}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-400 transition-colors"
                    disabled={optionCount >= 5}
                  >
                    +
                  </button>
                </div>
                <Text
                  variant="p"
                  className="text-xs text-gray-500 dark:text-gray-400"
                >
                  {t("visualMatching.create.questionSettings.maxOptions")}: 5
                </Text>
                {errors.optionCount && (
                  <Text variant="p" className="text-red-500 text-sm">
                    {errors.optionCount}
                  </Text>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              size="md"
              rounded="sm"
              onClick={() => navigate("/content/visual-matching")}
              className="border-gray-300 dark:border-gray-600"
            >
              {t("common.cancel")}
            </Button>
            <Button
              size="md"
              rounded="sm"
              onClick={handleSubmit}
              disabled={isMutating}
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
            >
              {isMutating
                ? t("common.saving")
                : t("visualMatching.create.submit")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateVisualMatchingView;
